
import React from "react";
import { SwapRequest } from "@/services/myReagents";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare } from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";

interface SwapResponseDialogProps {
  selectedRequest: SwapRequest | null;
  action: "approve" | "reject" | null;
  message: string;
  isSubmitting: boolean;
  onMessageChange: (message: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const SwapResponseDialog: React.FC<SwapResponseDialogProps> = ({
  selectedRequest,
  action,
  message,
  isSubmitting,
  onMessageChange,
  onSubmit,
  onClose
}) => {
  const isOpen = selectedRequest !== null && action !== null;
  
  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {action === "approve" ? "Approve" : "Reject"} Swap Request
          </DialogTitle>
          <DialogDescription>
            {action === "approve"
              ? "The requester will be notified that you've approved their swap request."
              : "Please provide a reason for rejecting this swap request."}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <label className="block text-sm font-medium mb-2">
            Message to the requester:
          </label>
          <Textarea
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder={action === "approve" 
              ? "E.g., Thanks for your interest! Let's coordinate a meetup time." 
              : "E.g., Sorry, I've decided to keep this reagent for now."
            }
            className="min-h-[100px]"
          />
        </div>
        
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button 
            onClick={onSubmit}
            disabled={isSubmitting}
            className={action === "approve" ? "" : "bg-destructive hover:bg-destructive/90"}
          >
            {isSubmitting ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <MessageSquare className="h-4 w-4 mr-1" />
                {action === "approve" ? "Approve & Send Message" : "Reject & Send Message"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SwapResponseDialog;
